import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { banksAPI } from '../../services/api';

const Banks = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [formData, setFormData] = useState({
    bank_name: '',
    account_name: '',
    account_number: '',
    clabe: '',
    holder_name: ''
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      setLoading(true);
      const response = await banksAPI.getAll();
      setBanks(response.data.banks);
    } catch (err) {
      setError('Error al cargar los datos bancarios');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (bank = null) => {
    if (bank) {
      setEditingBank(bank);
      setFormData({
        bank_name: bank.bank_name,
        account_name: bank.account_name || '',
        account_number: bank.account_number || '',
        clabe: bank.clabe,
        holder_name: bank.holder_name
      });
    } else {
      setEditingBank(null);
      setFormData({
        bank_name: '',
        account_name: '',
        account_number: '',
        clabe: '',
        holder_name: ''
      });
    }
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBank(null);
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    try {
      if (editingBank) {
        await banksAPI.update(editingBank.id, formData);
      } else {
        await banksAPI.create(formData);
      }
      closeModal();
      fetchBanks();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bank, force = false) => {
    const confirmMsg = force
      ? `¿ELIMINAR FORZADO el banco "${bank.bank_name}"?\n\nEsto eliminará la asociación con las webs que lo usan. Esta acción no se puede deshacer.`
      : `¿Eliminar cuenta bancaria de ${bank.bank_name}?`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      await banksAPI.delete(bank.id, force);
      fetchBanks();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Error al eliminar';
      if (errorMsg.includes('asociado') && !force) {
        if (window.confirm(`${errorMsg}\n\n¿Desea eliminar forzadamente y quitar la asociación de las webs?`)) {
          handleDelete(bank, true);
        }
      } else {
        alert(errorMsg);
      }
    }
  };

  return (
    <AdminLayout>
      <>
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Datos Bancarios</h1>
            <p className="text-gray-600">Gestiona las cuentas bancarias para las cotizaciones</p>
          </div>
          <button onClick={() => openModal()} className="btn btn-primary">
            + Agregar Cuenta
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Cargando...</p>
            </div>
          ) : banks.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No hay cuentas bancarias registradas
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Banco
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre de Cuenta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Número de Cuenta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CLABE
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Titular
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {banks.map((bank) => (
                  <tr key={bank.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{bank.bank_name}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                      {bank.account_name || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                      {bank.account_number || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600 font-mono text-sm">
                      {bank.clabe}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                      {bank.holder_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => openModal(bank)}
                        className="text-primary-600 hover:text-primary-800 mr-3"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(bank)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingBank ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}
              </h2>

              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {formError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="label">Banco</label>
                  <input
                    type="text"
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    className="input"
                    placeholder="Ej: HSBC, BBVA, Santander"
                    required
                  />
                </div>

                <div>
                  <label className="label">Nombre de la Cuenta</label>
                  <input
                    type="text"
                    value={formData.account_name}
                    onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                    className="input"
                    placeholder="Ej: HSBC Principal, BBVA Nómina"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Identificador para diferenciar cuentas del mismo banco</p>
                </div>

                <div>
                  <label className="label">Número de Cuenta <span className="text-gray-400 font-normal">(Opcional)</span></label>
                  <input
                    type="text"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    className="input"
                    placeholder="Ej: 6636604379"
                  />
                </div>

                <div>
                  <label className="label">CLABE Interbancaria (18 dígitos)</label>
                  <input
                    type="text"
                    value={formData.clabe}
                    onChange={(e) => setFormData({ ...formData, clabe: e.target.value.replace(/\D/g, '').slice(0, 18) })}
                    className="input font-mono"
                    placeholder="Ej: 021190066366043799"
                    maxLength={18}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.clabe.length}/18 dígitos</p>
                </div>

                <div>
                  <label className="label">Titular de la Cuenta</label>
                  <input
                    type="text"
                    value={formData.holder_name}
                    onChange={(e) => setFormData({ ...formData, holder_name: e.target.value })}
                    className="input"
                    placeholder="Nombre completo del titular"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={closeModal} className="btn btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" disabled={saving} className="btn btn-primary">
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Banks;
